import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PanelLoading } from "../../../components/Loading";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { useConfirm } from "../../../components/ConfirmDialog";
import { useAuth } from "../../../auth/AuthContext";
import { localizedName } from "../../../lib/localizedName";
import Ic from "../../../components/icons.jsx";
import type { ApiError } from "../../../api/client";
import { SUPPORTED_LOCALES } from "../../../i18n";
import { qualityApi, type QualityParameter } from "../../../api/quality";
import {
  criterionText,
  qualityClassesApi,
  type QualityClass,
  type QualityClassCriterionOperator,
  type SaveQualityClassBody,
} from "../../../api/qualityClasses";

const OPERATORS: QualityClassCriterionOperator[] = ["LT", "LTE", "GT", "GTE", "EQ", "ABSENT", "PRESENT"];

/**
 * The raw-material class codebook.
 *
 * Classes were already in the database — they came out of the quality service's own answers
 * — and nothing in the portal showed them. This is that screen.
 *
 * It leads with the unloading place and the permitted use rather than with the name, because
 * that is what a class is for: it decides which silo a lorry is sent to and what the material
 * may afterwards be made into. The price column is shown and nothing acts on it; the note on
 * screen says so, because whether a class bears on the price is still an open question
 * between the quality service and procurement.
 */
export default function QualityClassesPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const locale = i18n.resolvedLanguage ?? "sr";
  const canManage = hasPermission("quality_classes:manage");
  const canDelete = hasPermission("section:quality_classes:full");

  const [classes, setClasses] = useState<QualityClass[] | null>(null);
  const [cultures, setCultures] = useState<string[]>([]);
  const [culture, setCulture] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<QualityClass | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setClasses(
        await qualityClassesApi.list({
          culture: culture || undefined,
          includeInactive: includeInactive ? "true" : undefined,
        }),
      );
    } catch (e) {
      setError((e as ApiError).message);
    }
  }, [culture, includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void qualityClassesApi.cultures().then(setCultures).catch(() => undefined);
  }, []);

  async function retire(c: QualityClass) {
    const ok = await confirm({
      message: t(
        "quality.classes.confirmRetire",
        "Retire class {{code}}? Loads already classed keep it; it stops being offered.",
        { code: c.code },
      ),
    });
    if (!ok) return;
    try {
      await qualityClassesApi.retire(c.id);
      await load();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (error) {
    return (
      <div className="page">
        <p style={{ color: "var(--err)" }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="page fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("quality.classes.title", "Raw-material classes")}</h1>
          <p className="sub">
            {t(
              "quality.classes.subtitle",
              "The class a load is put into on intake. It decides which silo the lorry is sent to and what the material may be used for.",
            )}
          </p>
        </div>
        <div className="page-actions">
          {cultures.length > 0 && (
            <Select<string>
              value={culture}
              onChange={setCulture}
              ariaLabel={t("quality.classes.allCultures", "All crops")}
              style={{ minWidth: 180 }}
              options={[
                { value: "", label: t("quality.classes.allCultures", "All crops") },
                ...cultures.map((c) => ({ value: c, label: c })),
              ]}
            />
          )}
          <div className="seg">
            <button
              type="button"
              className="btn sm"
              aria-pressed={!includeInactive}
              onClick={() => setIncludeInactive(false)}
            >
              {t("quality.classes.activeOnly", "Active")}
            </button>
            <button
              type="button"
              className="btn sm"
              aria-pressed={includeInactive}
              onClick={() => setIncludeInactive(true)}
            >
              {t("quality.classes.all", "All")}
            </button>
          </div>
          {canManage && (
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              <Ic.plus /> {t("quality.classes.new", "New class")}
            </button>
          )}
        </div>
      </div>

      {!classes ? (
        <PanelLoading text={t("common.loading", "Loading…")} />
      ) : classes.length === 0 ? (
        <p className="empty">{t("quality.classes.empty", "No classes defined.")}</p>
      ) : (
        <section className="card">
          <div className="card-b">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("quality.classes.col.culture", "Crop")}</th>
                  <th>{t("quality.classes.col.code", "Class")}</th>
                  <th>{t("quality.classes.col.name", "Name")}</th>
                  <th>{t("quality.classes.col.unloadingPlace", "Unloaded at")}</th>
                  <th>{t("quality.classes.col.usedFor", "May be used for")}</th>
                  <th>{t("quality.classes.col.criteria", "What distinguishes it")}</th>
                  <th>{t("quality.classes.col.affectsPrice", "Bears on price")}</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {classes.map((c) => (
                  <tr key={c.id}>
                    <td data-label={t("quality.classes.col.culture", "Crop")}>{c.culture}</td>
                    <td data-label={t("quality.classes.col.code", "Class")}>
                      <code>{c.code}</code>
                      {!c.active && (
                        <>
                          {" "}
                          <span className="tag neutral">{t("quality.classes.retired", "Retired")}</span>
                        </>
                      )}
                    </td>
                    <td data-label={t("quality.classes.col.name", "Name")}>
                      {localizedName({ name: c.code, nameI18n: c.nameI18n }, locale)}
                    </td>
                    <td data-label={t("quality.classes.col.unloadingPlace", "Unloaded at")}>
                      {c.unloadingPlace ?? <span className="muted">—</span>}
                    </td>
                    <td data-label={t("quality.classes.col.usedFor", "May be used for")}>
                      {c.usedFor ?? <span className="muted">—</span>}
                    </td>
                    <td data-label={t("quality.classes.col.criteria", "What distinguishes it")}>
                      {c.criteria.length === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                          {c.criteria.map((cr) => (
                            <li key={cr.id} style={{ fontSize: 12.5 }}>
                              {criterionText(
                                cr,
                                localizedName(
                                  { name: cr.parameterKey ?? "", nameI18n: cr.parameterNameI18n },
                                  locale,
                                ),
                              )}
                              {cr.descriptive && (
                                <>
                                  {" "}
                                  <span className="tag neutral">
                                    {t("quality.classes.byEye", "confirmed by eye")}
                                  </span>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td data-label={t("quality.classes.col.affectsPrice", "Bears on price")}>
                      <span className="tag neutral">
                        {c.affectsPrice ? t("common.yes", "Yes") : t("common.no", "No")}
                      </span>
                    </td>
                    {canManage && (
                      <td style={{ textAlign: "right" }}>
                        <button type="button" className="btn sm" onClick={() => setEditing(c)}>
                          {t("action.edit", "Edit")}
                        </button>
                        {c.active && (
                          <button type="button" className="btn sm ghost" disabled={!canDelete} onClick={() => void retire(c)}>
                            {t("quality.classes.retire", "Retire")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="muted" style={{ fontSize: 12, margin: 0 }}>
        {t(
          "quality.classes.priceOpenQuestion",
          "The price column is recorded and nothing acts on it: whether a class bears on what a co-operative is paid has not been settled, so no settlement reads it.",
        )}
      </p>

      {canManage && (creating || editing) && (
        <ClassDialog
          value={editing}
          cultures={cultures}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Create or edit a class, with its criteria.
 *
 * The crop and the code are fixed once the class exists: loads already classed refer to it
 * by them, and renaming it would rewrite what those loads were judged to be.
 */
function ClassDialog({
  value,
  cultures,
  onClose,
  onSaved,
}: {
  value: QualityClass | null;
  cultures: string[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const locale = i18n.resolvedLanguage ?? "sr";
  const { hasPermission } = useAuth();
  const canDelete = hasPermission("section:quality_classes:full");
  const editing = value != null;

  const [culture, setCulture] = useState(value?.culture ?? (cultures[0] ?? "SOJA"));
  const [code, setCode] = useState(value?.code ?? "");
  const [names, setNames] = useState<Record<string, string>>({ ...(value?.nameI18n ?? {}) });
  const [unloadingPlace, setUnloadingPlace] = useState(value?.unloadingPlace ?? "");
  const [usedFor, setUsedFor] = useState(value?.usedFor ?? "");
  const [affectsPrice, setAffectsPrice] = useState(value?.affectsPrice ?? false);
  const [sortOrder, setSortOrder] = useState(String(value?.sortOrder ?? 0));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The criteria editor needs the test catalogue; a failure leaves the descriptive form usable.
  const [parameters, setParameters] = useState<QualityParameter[]>([]);
  useEffect(() => {
    void qualityApi.listParameters(true).then(setParameters).catch(() => undefined);
  }, []);

  const [critKind, setCritKind] = useState<"measurable" | "descriptive">("measurable");
  const [critParameter, setCritParameter] = useState("");
  const [critOperator, setCritOperator] = useState<QualityClassCriterionOperator>("LTE");
  const [critValue, setCritValue] = useState("");
  const [critDescription, setCritDescription] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!culture.trim() || !code.trim()) {
      setErr(t("quality.classes.error.cultureAndCode", "The crop and the class code are required."));
      return;
    }
    const body: SaveQualityClassBody = {
      culture: culture.trim(),
      code: code.trim(),
      nameI18n: names,
      unloadingPlace: unloadingPlace.trim() || null,
      usedFor: usedFor.trim() || null,
      affectsPrice,
      sortOrder: Number(sortOrder) || 0,
      active: value?.active ?? true,
    };
    setBusy(true);
    setErr(null);
    try {
      if (editing) await qualityClassesApi.update(value.id, body);
      else await qualityClassesApi.create(body);
      toast.success(t("quality.classes.toast.saved", "Class saved."));
      await onSaved();
    } catch (e2) {
      setErr((e2 as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function addCriterion() {
    if (!editing) return;
    const needsValue = critOperator !== "ABSENT" && critOperator !== "PRESENT";
    try {
      await qualityClassesApi.addCriterion(value.id, {
        parameterId: critKind === "measurable" ? critParameter : null,
        operator: critKind === "measurable" ? critOperator : null,
        valueNum: critKind === "measurable" && needsValue ? Number(critValue) : null,
        description: critKind === "descriptive" ? critDescription.trim() : null,
        sortOrder: (value.criteria.length + 1) * 10,
      });
      setCritValue("");
      setCritDescription("");
      await onSaved();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  async function removeCriterion(criterionId: string) {
    if (!editing) return;
    try {
      await qualityClassesApi.removeCriterion(value.id, criterionId);
      await onSaved();
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  return (
    <div className="modal-overlay" style={overlay} role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal-panel" style={dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0, fontSize: 16 }}>
          {editing
            ? t("quality.classes.edit", "Edit class")
            : t("quality.classes.new", "New class")}
        </h3>
        {err && <div className="tag err" style={{ display: "block", padding: 10 }}>{err}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Field label={t("quality.classes.field.culture", "Crop")} required>
              <input
                className="input"
                value={culture}
                onChange={(e) => setCulture(e.target.value)}
                disabled={editing}
                required
                style={{ width: "100%" }}
              />
            </Field>
            <Field label={t("quality.classes.field.code", "Class code")} required>
              <input
                className="input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={editing}
                required
                style={{ width: "100%" }}
              />
            </Field>
            <Field label={t("quality.classes.field.sortOrder", "Order")}>
              <input
                className="input"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{ width: "100%" }}
              />
            </Field>
          </div>

          <Field label={t("quality.classes.field.unloadingPlace", "Unloaded at")}>
            <input
              className="input"
              value={unloadingPlace}
              onChange={(e) => setUnloadingPlace(e.target.value)}
              placeholder={t("quality.classes.field.unloadingPlaceHint", "Which silo or bay")}
              style={{ width: "100%" }}
            />
          </Field>
          <Field label={t("quality.classes.field.usedFor", "May be used for")}>
            <input
              className="input"
              value={usedFor}
              onChange={(e) => setUsedFor(e.target.value)}
              placeholder={t("quality.classes.field.usedForHint", "Which products this material may go into")}
              style={{ width: "100%" }}
            />
          </Field>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={affectsPrice} onChange={(e) => setAffectsPrice(e.target.checked)} />
            <span>{t("quality.classes.field.affectsPrice", "This class bears on the price")}</span>
          </label>
          <span className="muted" style={{ fontSize: 11.5 }}>
            {t(
              "quality.classes.field.affectsPriceHint",
              "Recorded only. No settlement reads it: whether a class bears on the price has not been settled yet.",
            )}
          </span>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
              {t("quality.classes.field.names", "Name per language")}
            </span>
            {SUPPORTED_LOCALES.map((l) => (
              <div key={l} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span className="muted" style={{ width: 28, fontSize: 12 }}>{l}</span>
                <input
                  className="input"
                  value={names[l] ?? ""}
                  onChange={(e) => setNames((prev) => ({ ...prev, [l]: e.target.value }))}
                  style={{ flex: 1 }}
                />
              </div>
            ))}
          </div>

          {editing && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
              <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {t("quality.classes.field.criteria", "What distinguishes this class")}
              </span>
              <span className="muted" style={{ fontSize: 11.5 }}>
                {t(
                  "quality.classes.criteriaHint",
                  "Only what sets this class apart. The limits every class shares belong to the test plan.",
                )}
              </span>
              {value.criteria.map((cr) => (
                <div key={cr.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <span style={{ flex: 1 }}>
                    {criterionText(
                      cr,
                      localizedName({ name: cr.parameterKey ?? "", nameI18n: cr.parameterNameI18n }, locale),
                    )}
                  </span>
                  <button type="button" className="btn sm ghost" disabled={!canDelete} onClick={() => void removeCriterion(cr.id)}>
                    {t("action.remove", "Remove")}
                  </button>
                </div>
              ))}

              <div className="seg">
                <button
                  type="button"
                  className="btn sm"
                  aria-pressed={critKind === "measurable"}
                  onClick={() => setCritKind("measurable")}
                >
                  {t("quality.classes.criterion.measurable", "Measured")}
                </button>
                <button
                  type="button"
                  className="btn sm"
                  aria-pressed={critKind === "descriptive"}
                  onClick={() => setCritKind("descriptive")}
                >
                  {t("quality.classes.criterion.descriptive", "Confirmed by eye")}
                </button>
              </div>

              {critKind === "measurable" ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8 }}>
                  <Select<string>
                    value={critParameter}
                    onChange={setCritParameter}
                    ariaLabel={t("quality.classes.criterion.test", "Test")}
                    placeholder={t("quality.classes.criterion.pickTest", "— Pick the test —")}
                    options={parameters.map((p) => ({
                      value: p.id,
                      label: localizedName({ name: p.key, nameI18n: p.nameI18n }, locale),
                    }))}
                  />
                  <Select<string>
                    value={critOperator}
                    onChange={(v) => setCritOperator(v as QualityClassCriterionOperator)}
                    ariaLabel={t("quality.classes.criterion.operator", "Operator")}
                    options={OPERATORS.map((o) => ({ value: o, label: o }))}
                  />
                  {critOperator !== "ABSENT" && critOperator !== "PRESENT" && (
                    <input
                      className="input"
                      type="number"
                      step="0.001"
                      value={critValue}
                      onChange={(e) => setCritValue(e.target.value)}
                      aria-label={t("quality.classes.criterion.value", "Value")}
                    />
                  )}
                </div>
              ) : (
                <input
                  className="input"
                  value={critDescription}
                  onChange={(e) => setCritDescription(e.target.value)}
                  placeholder={t(
                    "quality.classes.criterion.descriptionHint",
                    "What the laboratory confirms by eye",
                  )}
                />
              )}
              <button
                type="button"
                className="btn sm"
                onClick={() => void addCriterion()}
                disabled={
                  critKind === "measurable" ? !critParameter : !critDescription.trim()
                }
              >
                <Ic.plus /> {t("quality.classes.criterion.add", "Add criterion")}
              </button>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>
              {t("action.cancel", "Cancel")}
            </button>
            <button type="submit" className="btn primary" disabled={busy}>
              {busy ? "…" : t("action.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16,
};

const dialog: React.CSSProperties = {
  width: "100%", maxWidth: 560, maxHeight: "85vh", overflowY: "auto",
  background: "var(--surface)", padding: 24, borderRadius: 12,
  boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14,
};

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}
